import Request from "../Request";
import { ClientLevelItem, LocationLevelItem, CategoryLevelItem, CategoryLocationLevelItem } from "../../interfaces/dashboards/PerformanceDashboard.interface";

class PerformanceDashboardService {
  private static readonly CACHE_TTL_MS = 5 * 60 * 1000;

  private static readonly responseCache = new Map<string, { expiresAt: number; value: any }>();

  private static readonly pendingRequests = new Map<string, Promise<any>>();

  private static buildCacheKey(scope: string, params: Record<string, any>) {
    const normalized = Object.keys(params).sort().reduce((accumulator: Record<string, any>, key) => {
      const value = params[key];
      accumulator[key] = Array.isArray(value) ? [...value].sort() : value;
      return accumulator;
    }, {});

    return `${scope}:${JSON.stringify(normalized)}`;
  }

  private static async withCache<T>(scope: string, params: Record<string, any>, loader: () => Promise<T>): Promise<T> {
    const cacheKey = this.buildCacheKey(scope, params);
    const now = Date.now();
    const cached = this.responseCache.get(cacheKey);

    if (cached && cached.expiresAt > now) {
      return cached.value as T;
    }

    const pending = this.pendingRequests.get(cacheKey);
    if (pending) {
      return pending as Promise<T>;
    }

    const request = loader()
      .then((value) => {
        this.responseCache.set(cacheKey, {
          value,
          expiresAt: Date.now() + this.CACHE_TTL_MS,
        });
        this.pendingRequests.delete(cacheKey);
        return value;
      })
      .catch((error) => {
        this.pendingRequests.delete(cacheKey);
        throw error;
      });

    this.pendingRequests.set(cacheKey, request);
    return request;
  }

  private static applyDashboardFilterParams(
    params: Record<string, any>,
    {
      locations = [],
      categories = [],
      classes = [],
      locationExclude = false,
      categoryExclude = false,
      classExclude = false,
    }: {
      locations?: string[];
      categories?: string[];
      classes?: string[];
      locationExclude?: boolean;
      categoryExclude?: boolean;
      classExclude?: boolean;
    },
  ) {
    if (locations && locations.length > 0) {
      params.locations = locations.join(',');
      if (locationExclude) params.locationsExclude = '1';
    }
    if (categories && categories.length > 0) {
      params.categories = categories.join(',');
      if (categoryExclude) params.categoriesExclude = '1';
    }
    if (classes && classes.length > 0) {
      params.classes = classes.join(',');
      if (classExclude) params.classesExclude = '1';
    }
  }

  static async getSimpleDashboardDataForClient({ clientId, epochMonth = null, compareEpochMonth = null, locations = [], categories = [], classes = [], locationExclude = false, categoryExclude = false, classExclude = false }: { clientId: string | number, epochMonth: number | null, compareEpochMonth: number | null, locations?: string[], categories?: string[], classes?: string[], locationExclude?: boolean, categoryExclude?: boolean, classExclude?: boolean }) {
    try {
      const params: any = epochMonth && compareEpochMonth ? { targetEpochMonth: epochMonth, compareEpochMonth } : {};

      this.applyDashboardFilterParams(params, { locations, categories, classes, locationExclude, categoryExclude, classExclude });

      return await this.withCache('simple-dashboard-data', { clientId, ...params }, async () => {
        //todo: client progress is using old simple dashboard sql but it should not.
        return Request.get(`/api/clients/${clientId}/simple-dashboard-data`, params);
      })
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async getMonthlyRecapData({ clientId, epochMonth, locations = [], categories = [], classes = [], locationExclude = false, categoryExclude = false, classExclude = false }: { clientId: string | number, epochMonth: number, locations?: string[], categories?: string[], classes?: string[], locationExclude?: boolean, categoryExclude?: boolean, classExclude?: boolean }) {
    try {
      const params: any = { epochMonth }

      this.applyDashboardFilterParams(params, { locations, categories, classes, locationExclude, categoryExclude, classExclude })

      return await this.withCache('monthly-recap-data', { clientId, ...params }, async () => Request.get(`/api/clients/${clientId}/monthly-recap-data`, params));
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async getPerformanceDashboardKeyMetricTrendData({ clientId, startEpochMonth, endEpochMonth, locations = [], categories = [], classes = [], locationExclude = false, categoryExclude = false, classExclude = false }: { clientId: string | number, startEpochMonth: number, endEpochMonth: number, locations?: string[], categories?: string[], classes?: string[], locationExclude?: boolean, categoryExclude?: boolean, classExclude?: boolean }) {
    const params: any = { startEpochMonth, endEpochMonth };

    this.applyDashboardFilterParams(params, { locations, categories, classes, locationExclude, categoryExclude, classExclude });

    try {
      return await this.withCache('performance-key-metric-trend-data', { clientId, ...params }, async () => Request.get(`/api/clients/${clientId}/performance-key-metric-trend-data`, params, false, true));
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async getPerformanceDashboardTransactionTrendData({ clientId, startEpochMonth, endEpochMonth }: { clientId: string | number, startEpochMonth: number, endEpochMonth: number }) {
    try {
      return await Request.get(
        `/api/clients/${clientId}/performance-transaction-trend-data`,
        { startEpochMonth, endEpochMonth },
        false,
        true,
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async getPerformanceDashboardDailySalesTrendData({ clientId, epochMonth, performers }: { clientId: string | number, epochMonth: number, performers: Array<{ locationCode: string, classCode: string, category: string, seriesLabel: string }> }) {
    try {
      return await Request.get(
        `/api/clients/${clientId}/performance-daily-sales-trend-data`,
        { epochMonth, performers: JSON.stringify(performers) },
        false,
        true,
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async getPerformanceDashboardBestPerformersData({
    clientId,
    epochMonth,
    groupBy,
    locations = [],
    categories = [],
    classes = [],
    locationExclude = false,
    categoryExclude = false,
    classExclude = false,
    limit,
    range,
  }: {
    clientId: string | number;
    epochMonth: number;
    groupBy: 'Vendor' | 'Product Type' | 'Items' | 'Vendor > Class';
    locations?: string[];
    categories?: string[];
    classes?: string[];
    locationExclude?: boolean;
    categoryExclude?: boolean;
    classExclude?: boolean;
    limit?: number;
    range?: string;
  }) {
    try {
      const params: any = { epochMonth, groupBy };
      if (range) {
        params.range = range;
      }
      this.applyDashboardFilterParams(params, { locations, categories, classes, locationExclude, categoryExclude, classExclude });
      if (limit) {
        params.limit = limit;
      }

      return await Request.get(
        `/api/clients/${clientId}/performance-best-performers-data`,
        params,
        false,
        true,
      );
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async getInventoryHealthData({ clientId, epochMonth, beginningPeriod, compareEpochMonth, locations = [], categories = [], classes = [], locationExclude = false, categoryExclude = false, classExclude = false, limit, endingPeriod }: { clientId: string | number, epochMonth: number, beginningPeriod?: number, compareEpochMonth: number, locations?: string[], categories?: string[], classes?: string[], locationExclude?: boolean, categoryExclude?: boolean, classExclude?: boolean, limit?: number, endingPeriod?: number }) {
    try {
      const params: any = { targetEpochMonth: epochMonth, compareEpochMonth };

      if (beginningPeriod) {
        params.beginningPeriod = beginningPeriod;
      }

      this.applyDashboardFilterParams(params, { locations, categories, classes, locationExclude, categoryExclude, classExclude });
      if (limit) {
        params.limit = limit;
      }
      if (endingPeriod) {
        params.endingPeriod = endingPeriod;
      }

      // Debug: Log API call parameters
      console.log('API Call - getInventoryHealthData:', {
        url: `/api/clients/${clientId}/inventory-health-data`,
        params,
        rawFilters: { locations, categories, classes }
      });

      return await this.withCache('inventory-health-data', { clientId, ...params }, async () => Request.get(`/api/clients/${clientId}/inventory-health-data`, params))
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async getFastestSellThroughData({ clientId, epochMonth, beginningPeriod, compareEpochMonth, locations = [], categories = [], classes = [], locationExclude = false, categoryExclude = false, classExclude = false, limit }: { clientId: string | number, epochMonth: number, beginningPeriod: number, compareEpochMonth: number, locations?: string[], categories?: string[], classes?: string[], locationExclude?: boolean, categoryExclude?: boolean, classExclude?: boolean, limit?: number }) {
    try {
      const params: any = { targetEpochMonth: epochMonth, beginningPeriod, compareEpochMonth };

      this.applyDashboardFilterParams(params, { locations, categories, classes, locationExclude, categoryExclude, classExclude });
      if (limit) {
        params.limit = limit;
      }

      return await this.withCache('fastest-sell-through-data', { clientId, ...params }, async () => Request.get(`/api/clients/${clientId}/fastest-sell-through-data`, params))
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async getSlowestSellThroughData({ clientId, epochMonth, beginningPeriod, compareEpochMonth, locations = [], categories = [], classes = [], locationExclude = false, categoryExclude = false, classExclude = false, limit }: { clientId: string | number, epochMonth: number, beginningPeriod: number, compareEpochMonth: number, locations?: string[], categories?: string[], classes?: string[], locationExclude?: boolean, categoryExclude?: boolean, classExclude?: boolean, limit?: number }) {
    try {
      const params: any = { targetEpochMonth: epochMonth, beginningPeriod, compareEpochMonth };

      this.applyDashboardFilterParams(params, { locations, categories, classes, locationExclude, categoryExclude, classExclude });
      if (limit) {
        params.limit = limit;
      }

      return await this.withCache('slowest-sell-through-data', { clientId, ...params }, async () => Request.get(`/api/clients/${clientId}/slowest-sell-through-data`, params))
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async getDropdownOptions({ clientId, epochMonth }: { clientId: string | number, epochMonth: number }) {
    try {
      const params = { targetEpochMonth: epochMonth };
      return await this.withCache('dropdown-options', { clientId, ...params }, async () => Request.get(`/api/clients/${clientId}/dropdown-options`, params))
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static async getOpenToBuyKpiData({
    clientId,
    currentEpochMonth,
    endingEpochMonth,
    calculationMode,
    locations = [],
    categories = [],
    classes = [],
    locationExclude = false,
    categoryExclude = false,
    classExclude = false,
  }: {
    clientId: string | number,
    currentEpochMonth: number,
    endingEpochMonth: number,
    calculationMode: 'mtd' | 'eom',
    locations?: string[],
    categories?: string[],
    classes?: string[],
    locationExclude?: boolean,
    categoryExclude?: boolean,
    classExclude?: boolean,
  }) {
    const params: any = { currentEpochMonth, endingEpochMonth, calculationMode };
    this.applyDashboardFilterParams(params, { locations, categories, classes, locationExclude, categoryExclude, classExclude });

    try {
      return await this.withCache('open-to-buy-kpi-data', { clientId, ...params }, async () => Request.get(`/api/clients/${clientId}/open-to-buy-kpi-data`, params));
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  static moneyFormat(value: number, wholeNumber: boolean = true): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: wholeNumber ? 0 : 2,
      maximumFractionDigits: wholeNumber ? 0 : 2
    }).format(value);
  }

  static percentageFormat(value: number, wholeNumber: boolean = true): string {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: wholeNumber ? 0 : 2,
      maximumFractionDigits: wholeNumber ? 0 : 2
    }).format(value);
  }

  static calculateFreshness(inflow: number, inventory: number) {
    return inventory ? (100 * inflow / inventory) : 0;
  }

  static calculateWeeksOnHand(sales: number, inventory: number) {
    if (!sales || !inventory) {
      return `0 weeks`;
    }
    return `${(52 / (sales / inventory)).toFixed(0)} weeks`;
  }

  static calculateMissedSales(inventoryPlan: number, inventory: number, sales: number) {
    if (!inventoryPlan || !inventory || !sales) {
      return 0
    }
    const understocked = inventory - inventoryPlan;
    if (understocked > 0) {
      return 0
    }
    return understocked / (inventory / sales);
  }

  static calculateEfficiency({ salesSumLastMonth, inventory, inflow }: { salesSumLastMonth: number, inventory: number, inflow: number }): number {
    const inventoryPlusInflow = inventory + inflow;
    if (inventoryPlusInflow === 0) {
      return 0;
    }
    return salesSumLastMonth / inventoryPlusInflow;
  }

  /**
   * Converts location-level data to client-level data - either filtered or aggregated
   * @param locationLevelData Array of location data items
   * @param locationCode Optional location code to filter by
   * @returns Client-level data array
   */
  static convertLocationLevelToClientLevel(
    locationLevelData: LocationLevelItem[],
    locationCode?: string
  ): ClientLevelItem[] {
    // Filter data if locationCode provided
    const filteredData = locationCode
      ? locationLevelData.filter(item => item.locationCode === locationCode)
      : locationLevelData;

    // Group by epochMonthRm using Map
    const grouped = filteredData.reduce((map, item) => {
      const month = item.epochMonthRm;
      const existing = map.get(month) ?? {
        trend: 0,
        epochMonthSpan: month,
        inventoryBOMPlan: 0,
        inventoryBOMRM: 0,
        inventoryDeviationRm: 0,
        otbRetailEom: 0,
        salesPlan: 0,
        salesRm: 0,
        markdownsRm: 0,
        markdownsPlan: 0,
        inflowRetailRm: 0,
        inflowPlanRetailRm: 0,
        // LY fields
        salesLy: 0,
        markdownsLy: 0,
        inventoryBomLy: 0,
        inflowRetailLy: 0,
        inventoryDeviationCostRm: 0,
        otbCostEom: 0,
        // Additional inventory fields
        inventoryCurrent: 0,
        inventoryBomPlanNM: 0,
        inventoryEomLy: 0,
        markdownsRemaining: 0,
        onOrderRetailMTD: 0,
        onOrderCostMTD: 0,
        otbRetailMtd: 0,
        otbCostMtd: 0,
        costComplementMtd: 0,
        otbLessTrend: 0
      };

      // Sum all metrics in one operation
      const metrics = [
        'inventoryBOMPlan', 'inventoryBOMRM', 'inventoryDeviationRm',
        'otbRetailEom', 'salesPlan', 'salesRm',
        'markdownsRm', 'markdownsPlan', 'inflowRetailRm', 'inflowPlanRetailRm',
        'salesLy', 'markdownsLy', 'inventoryBomLy', 'inflowRetailLy',
        'inventoryDeviationCostRm', 'otbCostEom',
        // Additional inventory fields
        'inventoryCurrent', 'inventoryBomPlanNM', 'inventoryEomLy',
        'markdownsRemaining', 'onOrderRetailMTD', 'onOrderCostMTD',
        'otbRetailMtd', 'otbCostMtd', 'costComplementMtd',
        'otbLessTrend'
      ] as const;

      metrics.forEach(metric => {
        existing[metric] += (item[metric as keyof LocationLevelItem] as number) || 0;
      });

      // Get max of following
      const maxMetrics = [
        'trend',
      ] as const;

      maxMetrics.forEach(metric => {
        existing[metric] = Math.max(existing[metric], item[metric] || 0);
      });

      return map.set(month, existing as ClientLevelItem);
    }, new Map<number, ClientLevelItem>());

    return Array.from(grouped.values());
  }

  /**
   * Converts category-location level data to category level data
   * @param categoryLocationLevelData Array of category-location data items
   * @param locationCode Optional location code to filter by
   * @returns Category-level data array
   */
  static convertCategoryLocationLevelToCategoryLevel(
    categoryLocationLevelData: CategoryLocationLevelItem[],
    locationCode?: string
  ): CategoryLevelItem[] {
    // Filter data if locationCode provided
    const filteredData = locationCode
      ? categoryLocationLevelData.filter(item => item.locationCode === locationCode)
      : categoryLocationLevelData;

    // Group by epochMonthRm using Map
    const grouped = filteredData.reduce((map, item) => {
      const month = item.epochMonthRM;
      const existing = map.get(month) ?? {
        category: item.category,
        className: item.className,
        epochMonthRM: month,
        inventoryBomRm: 0,
        inventoryBomPlan: 0,
        inflowRetailRm: 0,
        salesSumLm: 0,
        inventoryBomAvgLm: 0,
        otbRetailEom: 0,
        salesRm: 0
      };

      // Sum all metrics in one operation
      const metrics = [
        'inventoryBomRm', 'inventoryBomPlan', 'inflowRetailRm',
        'salesSumLm', 'inventoryBomAvgLm', 'otbRetailEom', 'salesRm'
      ] as const;

      metrics.forEach(metric => {
        existing[metric] += item[metric] || 0;
      });

      return map.set(month, existing);
    }, new Map<number, CategoryLevelItem>());

    return Array.from(grouped.values());
  }
}

export default PerformanceDashboardService;